import { useEffect } from 'react';

import { getContext } from '@/context';
import { setAppStore } from '@/store';
import { useAuth } from './useAuth';

type CreditsState = {
  areCreditsAvailable?: boolean;
  areCreditsFetched?: boolean;
  areCreditsLoading?: boolean;
};

const normalizeCreditsState = (credits: CreditsState) => {
  if (credits.areCreditsAvailable === false) {
    return {
      ...credits,
      areCreditsFetched: true,
      areCreditsLoading: false,
    };
  }

  if (credits.areCreditsAvailable === true) {
    return {
      ...credits,
      areCreditsFetched: true,
      areCreditsLoading: false,
    };
  }

  return credits;
};

const useCreditsSubscription = (enabled = true) => {
  const { isAuthorized } = useAuth();

  useEffect(() => {
    // Reset credits state when user is not authorized
    if (!isAuthorized) {
      setAppStore({ areCreditsAvailable: null });
      return;
    }

    // Only subscribe when enabled
    if (!enabled) {
      return;
    }

    try {
      const context = getContext();
      context.monetization.updateCredits?.();

      const unsubscribe = context.monetization.credits.subscribe(
        (value: CreditsState) => {
          if (value?.areCreditsAvailable === undefined) {
            return;
          }

          const normalized = normalizeCreditsState({ ...value });

          const nextAvailability =
            typeof normalized.areCreditsAvailable === 'boolean'
              ? normalized.areCreditsAvailable
              : null;

          setAppStore({ areCreditsAvailable: nextAvailability });
        },
        { needPrevious: true }
      );

      return () => {
        unsubscribe?.();
      };
    } catch {
      // SDK not ready
    }
  }, [enabled, isAuthorized]);
};

export { useCreditsSubscription };
